import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod/v4";
import { useForm } from "react-hook-form";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useMutation } from "@tanstack/react-query";
import { HTTPError } from "ky";
import { getReferralCode, useRedirectBack } from "./-utils";
import type { GoogleOauthError } from "@/lib/hooks/google";
import { api } from "@/lib/http";
import { useGoogleOauthMutation } from "@/lib/hooks/google";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PasswordInput } from "@/components/ui/passwordInput";
import { passwordRegex, passwordRegexErrorMessage } from "@/lib/auth";
import { generateDefaultValue } from "@/lib/utils/zod";
import { getUtmParams } from "@/lib/utils/utm";

export const Route = createFileRoute("/auth/signup")({
  component: RouteComponent,
  staticData: {
    title: "Sign Up",
  },
});

const SignupFormSchema = z.object({
  full_name: z.string("Full Name is required").min(1, "Full Name is required"),
  email: z
    .string("Email or username is required")
    .min(1, "Email or username is required"),
  password: z
    .string("Password is required")
    .min(1, "Password is required")
    .regex(passwordRegex, passwordRegexErrorMessage),
  terms_conditions_aggreed: z
    .boolean()
    .refine((b) => b, "You must agree to the terms and conditions")
    .default(false),

  referrer_id: z.string().optional(),
});
type SignupFormSchema = z.infer<typeof SignupFormSchema>;

function RouteComponent() {
  const routeSearchParams = Route.useSearch();

  const navigate = useNavigate();
  const redirect = useRedirectBack();

  const form = useForm({
    resolver: standardSchemaResolver(SignupFormSchema),
    defaultValues: {
      ...generateDefaultValue(SignupFormSchema),
      referrer_id: getReferralCode() ?? undefined,
    },
  });

  const signup = useMutation({
    mutationFn: async (json: SignupFormSchema) => {
      const searchParams = new URLSearchParams();
      if (routeSearchParams.code)
        searchParams.set("appsumo_code", routeSearchParams.code);

      Object.entries(getUtmParams()).forEach(([key, value]) =>
        searchParams.set(key, value),
      );

      await api.auth.post("users/register", {
        json,
        searchParams,
      });
    },
    onSuccess(_data, { email }) {
      navigate({
        to: "/auth/verify-account",
        search: {
          email,
          redirect: routeSearchParams.redirect,
        },
      });
    },
    async onError(error: HTTPError) {
      const message = await error.response.json<{ detail?: string }>();
      form.setError("root.serverError", {
        message: message.detail || "An unexpected error occurred",
      });
    },
  });

  const googleSignup = useGoogleOauthMutation({
    async onToken(access_token) {
      const searchParams = new URLSearchParams();
      if (routeSearchParams.code)
        searchParams.set("appsumo_code", routeSearchParams.code);

      Object.entries(getUtmParams()).forEach(([key, value]) =>
        searchParams.set(key, value),
      );

      await api.auth.post("users/social-authentication", {
        json: { access_token, register: true },
        searchParams,
      });
      redirect();
    },
    async onError(error) {
      if (error instanceof HTTPError) {
        const message = await error.response.json<{ detail?: string }>();
        form.setError("root.serverError", {
          message: message.detail || "An unexpected error occurred",
        });
      } else if (
        error &&
        typeof error === "object" &&
        "error_description" in error
      ) {
        form.setError("root.serverError", {
          message: (error as GoogleOauthError).error_description,
        });
      } else {
        form.setError("root.serverError", {
          message: "An unexpected error occurred",
        });
      }
    },
  });

  return (
    <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center px-4 py-8">
      {/* Custom Tab Bar */}
      <div className="mb-8 w-full max-w-md">
        <div className="rounded-2xl flex w-full bg-gray-50 p-1 shadow-sm sm:flex-row dark:bg-[#18181b]">
          <Link
            to="/auth/signup"
            className={
              "w-full rounded-xl py-2 text-center text-lg font-semibold transition-all sm:flex-1 " +
              (window.location.pathname === "/auth/signup"
                ? "bg-white text-gray-600 shadow-md dark:bg-[#232329]"
                : "text-gray-500 hover:text-gray-600 dark:text-gray-400")
            }
          >
            Sign Up
          </Link>
          <Link
            to="/auth/signin"
            className={
              "w-full rounded-xl py-2 text-center text-lg font-semibold transition-all sm:flex-1 " +
              (window.location.pathname === "/auth/signin"
                ? "bg-white text-gray-700 shadow-md dark:bg-gray-700"
                : "text-gray-500 hover:text-gray-600 dark:text-gray-400")
            }
          >
            Sign In
          </Link>
        </div>
      </div>
      <div className="w-full space-y-8">
        <div className="space-y-2 text-center">
          <h1 className="text-4xl font-bold">Create an account</h1>
          <p className="text-base text-gray-500">Please enter your details.</p>
        </div>
        <Form {...form}>
          <form
            className="space-y-6"
            onSubmit={form.handleSubmit((formData) => {
              if (signup.isPending) return;
              signup.mutate(formData);
            })}
          >
            {/* Google Sign Up */}
            {/* <button
              type="button"
              className="flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 py-3 font-medium text-gray-700 transition hover:bg-gray-100 dark:border-gray-700 dark:text-white dark:hover:text-gray-700"
              disabled={signup.isPending || googleSignup.isPending}
              onClick={() => {
                if (googleSignup.isPending) return;
                googleSignup.mutate();
              }}
            >
              <img
                src="/icons/google-icon.svg"
                alt="Google logo"
                className="h-5 w-5"
              />
              {googleSignup.isPending ? "Signing Up..." : "Sign Up with Google"}
            </button> */}
            {/* OR Separator */}
            {/* <div className="my-4 flex items-center">
              <div className="mx-2 h-px flex-1 bg-gray-200 dark:bg-gray-700" />
              <span className="px-2 text-gray-500 dark:text-gray-400">or</span>
              <div className="mx-2 h-px flex-1 bg-gray-200 dark:bg-gray-700" />
            </div> */}
            {/* Full Name */}
            <FormField
              control={form.control}
              name="full_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-black dark:text-white">
                    Full Name <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="name"
                      placeholder="Full Name"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Email */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-black dark:text-white">
                    Email
                    <span className="text-red-500">*</span>{" "}
                  </FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="email"
                      placeholder="Email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Password */}
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-black dark:text-white">
                    Password
                    <span className="text-red-500">*</span>{" "}
                  </FormLabel>
                  <FormControl>
                    <PasswordInput
                      autoComplete="new-password"
                      placeholder="Password"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Must be at least 8 characters.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Terms and Conditions */}
            <FormField
              control={form.control}
              name="terms_conditions_aggreed"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-3">
                    <FormControl>
                      <Checkbox
                        className="mt-1 data-[state=checked]:border-gray-600 data-[state=checked]:bg-gray-700"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="text-sm leading-5">
                      <div>
                        <FormLabel className="m-0 font-normal text-black dark:text-white">
                          By signing up you agree to our
                        </FormLabel>
                      </div>
                      <div className="flex gap-1">
                        <a
                          href="https://polygraf.ai/terms-and-conditions/"
                          className="underline"
                        >
                          Terms and Conditions
                        </a>
                        <span>&</span>
                        <a
                          href="https://polygraf.ai/privacy-policy/"
                          className="underline"
                        >
                          Privacy Policy
                        </a>
                      </div>
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Submit Button */}
            <button
              type="submit"
              disabled={signup.isPending || googleSignup.isPending}
              className="mb-0 w-full rounded-md bg-gray-700 py-3 font-medium text-white transition-colors hover:bg-gray-600 disabled:opacity-60"
            >
              {signup.isPending ? "Creating Account..." : "Create Account"}
            </button>

            {/* Server Error */}
            {form.formState.errors.root?.serverError && (
              <p className="text-sm text-destructive">
                {form.formState.errors.root.serverError.message}
              </p>
            )}
          </form>
        </Form>

        {/* Sign In Redirect */}
        <p className="text-center text-sm text-gray-700 dark:text-white">
          Already have an account?{" "}
          <Link
            to="/auth/signin"
            search={{ redirect: routeSearchParams.redirect }}
            className="font-medium text-gray-600 hover:underline"
          >
            Sign in
          </Link>
        </p>
        <div className="flex items-center gap-4 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700">
            <img src="/favicon.ico" alt="Chrome" className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-900 dark:text-white">
              Get the Chrome Extension
            </div>
            <div className="text-xs text-gray-500">
              One-click access to all features in your browser.
            </div>
          </div>
          <a
            href="https://chromewebstore.google.com/detail/polygraf-ai-content-detec/cbngnnhgfljncoliddifmkgklphlojnf"
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 rounded-lg bg-gray-700 px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-gray-600"
          >
            Install
          </a>
        </div>
      </div>
    </main>
  );
}
